import { Flex, Skeleton } from "@mantine/core";
import { BoxTitle, Container } from "./StyledComponents";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";
import { theme, media } from "../../theme";
import styled, { keyframes } from "styled-components";
import { useFilterContext } from "../../context/DashboardFilterContext";
import { getLiveWeeklyAnalysis } from "../../api/dashboardApi";
import { useEffect, useState, useCallback } from "react";
import {
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ClockIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";

const HaftaSatiri = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
`;

const OkDugme = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #fff;
  color: #64748b;
  cursor: pointer;
  transition: background 0.2s ease;
  &:hover:not(:disabled) {
    background: #f1f5f9;
  }
  &:disabled {
    opacity: 0.35;
    cursor: default;
  }
`;

const HaftaMetin = styled.span`
  font-size: 12px;
  font-weight: 600;
  /* Sıkı satır yüksekliği Türkçe büyük harflerin tepesini kırpıyor. */
  line-height: 17px;
  color: #64748b;
  padding: 0 4px;
  white-space: nowrap;
`;

const BuHaftayaDon = styled.button`
  border: none;
  background: transparent;
  padding: 2px 6px;
  font-size: 11.5px;
  line-height: 16px;
  font-weight: 600;
  color: ${theme.colors.brand.normal};
  cursor: pointer;
  white-space: nowrap;
`;

const OtomatikNot = styled.div`
  margin-top: 3px;
  font-size: 11.5px;
  line-height: 16px;
  color: #94a3b8;
`;

const BosDurum = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-align: center;
  padding: 0 24px;
`;

const BosBaslik = styled.div`
  font-size: 15px;
  font-weight: 600;
  /* Sıkı satır yüksekliği Türkçe büyük harflerin tepesini kırpıyor. */
  line-height: 21px;
  color: #64748b;
`;

const BosMetin = styled.div`
  font-size: 12.5px;
  line-height: 18px;
  color: #94a3b8;
  max-width: 380px;
`;

const TooltipNot = styled.div`
  font-size: 11.5px;
  line-height: 16px;
  color: #94a3b8;
  margin-top: 2px;
`;

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(15px); }
  to { opacity: 1; transform: translateY(0); }
`;

/**
 * Günün üç hâli (backend `state` alanı, plan 2.60):
 *   measured → yaşandı + vardiya planı vardı; sayılar gerçek
 *   future   → gün henüz gelmedi
 *   noplan   → yaşandı ama o gün için vardiya planı yok
 *
 * ⚠ İkisini de sıfır çubuk olarak çizmek "kimse gelmedi" demekti — bu
 * widget'ın en büyük kusuru buydu. Ayrımı taşımayan hiçbir gösterim
 * kullanılmamalı.
 */
const DURUM_METNI: Record<string, string> = {
  future: "Gün henüz gelmedi",
  noplan: "Vardiya planı yok",
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  const gun = payload[0]?.payload ?? {};
  const aciklama = DURUM_METNI[gun.state];
  return (
    <TooltipBox>
      <TooltipLabel>{label}</TooltipLabel>
      {aciklama ? (
        <TooltipNot>{aciklama}</TooltipNot>
      ) : (
        <>
          {payload.map((p: any, i: number) => (
            <TooltipRow key={i} $color={p.fill}>
              <TooltipDot $color={p.fill} />
              <span>{p.name === "present" ? "Mevcut" : p.name === "earlyExit" ? "Erken Çıkış" : "Geç"}</span>
              <strong>{p.value}</strong>
            </TooltipRow>
          ))}
          <TooltipNot>{gun.expected} kişi bekleniyordu</TooltipNot>
        </>
      )}
    </TooltipBox>
  );
};

/**
 * Gün etiketi. Ölçülemeyen günler soluk yazılıyor ve altına sebebi
 * geçiyor — grafikte boş sütunun neden boş olduğu başka yerden anlaşılmıyor.
 *
 * ⚠ Recharts'ın `ReferenceArea`'sı yerine özel tick: kategorik eksende
 * x1===x2 bandının genişliği sürüme göre değişiyor, burada geometri
 * tahmini yapmak istemedik.
 */
const GunEtiketi = ({ x, y, payload, veri }: any) => {
  const gun = veri?.find((g: any) => g.day === payload?.value);
  const soluk = gun?.state === "future" || gun?.state === "noplan";
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        dy={12}
        textAnchor="middle"
        fill={soluk ? "#CBD5E1" : "#64748b"}
        fontSize={13}
        fontWeight={600}
      >
        {payload?.value}
      </text>
      {soluk && (
        <text dy={26} textAnchor="middle" fill="#CBD5E1" fontSize={9} fontWeight={500}>
          {gun.state === "future" ? "henüz" : "plan yok"}
        </text>
      )}
    </g>
  );
};

const WeeklyAttendanceAnalysis = ({ flex }: { flex: any }) => {
  const { filters } = useFilterContext();
  const [chartData, setChartData] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  /** 0 = bu hafta, -1 = geçen hafta … Sunucu 0'ın ötesine izin vermiyor. */
  const [weekOffset, setWeekOffset] = useState(0);
  const [aralik, setAralik] = useState<{ start?: string; end?: string }>({});
  /*
   * ⚠ Takvim haftasının bedeli: Pazartesi sabahı grafik neredeyse boş.
   * Tablo ancak Cuma akşamı doluyor, Pazartesi yeniden sıfırlanıyor — İK
   * her hafta başı boş bir sayfa görüyordu. Bu hafta hiç ölçülmüş gün
   * yoksa geçen haftaya KENDİLİĞİNDEN düşüyoruz ve bunu açıkça yazıyoruz;
   * sessizce başka bir haftayı göstermek grafiği yalancı yapardı.
   */
  const [otomatikGeri, setOtomatikGeri] = useState(false);
  /*
   * ⚠ İKİ AYRI BAYRAK. `otomatikGeri` "otomatik düşme bir daha DENENMESİN"
   * demek ve elle gezildiğinde de kapanıyor; `otomatikGosterim` ise "şu an
   * ekranda duran hafta otomatik seçildi" demek. Tek bayrakla yazılmıştı ve
   * okla geçen haftaya gidildiğinde "Bu hafta henüz veri yok" notu yalan
   * söylüyordu — bu hafta veri VARDI.
   */
  const [otomatikGosterim, setOtomatikGosterim] = useState(false);

  const getBarChartData = useCallback(
    async (offset: number, otomatikDenensin: boolean) => {
      setLoading(true);
      try {
        const response: any = await getLiveWeeklyAnalysis(filters, offset);
        if (response?.success) {
          const chart = response.data?.chart || [];
          const olculenVar = chart.some((g: any) => g.state === "measured");

          if (!olculenVar && otomatikDenensin && offset === 0) {
            // Bu hafta henüz boş → geçen haftayı getir (bir kez).
            setOtomatikGeri(true);
            setOtomatikGosterim(true);
            setWeekOffset(-1);
            return;
          }
          setChartData(chart);
          setStats(response.data?.stats);
          setAralik({ start: response.data?.weekStart, end: response.data?.weekEnd });
        }
      } catch (error) {
        console.error("Analiz verisi yüklenemedi", error);
      } finally {
        setLoading(false);
      }
    },
    [filters]
  );

  const AYLAR = ["Oca","Şub","Mar","Nis","May","Haz","Tem","Ağu","Eyl","Eki","Kas","Ara"];
  const gunAy = (iso?: string) => {
    if (!iso) return "";
    const [, ay, gun] = iso.split("-");
    return `${Number(gun)} ${AYLAR[Number(ay) - 1]}`;
  };
  const haftaEtiketi = aralik.start
    ? `${gunAy(aralik.start)} – ${gunAy(aralik.end)}` +
      (weekOffset === 0 ? " · bu hafta" : weekOffset === -1 ? " · geçen hafta" : "")
    : "—";

  // Yaşanmış + planı olan tek bir gün bile yoksa grafik yerine cümle çıkıyor.
  const olculenGunYok =
    chartData.length > 0 && !chartData.some((g: any) => g.state === "measured");

  useEffect(() => {
    getBarChartData(weekOffset, !otomatikGeri && weekOffset === 0);
  }, [getBarChartData, weekOffset, otomatikGeri]);

  return (
    <Container $flex={flex} style={{ display: "flex", flexDirection: "column" }}>
      <HeaderRow>
        <Flex align="center" gap={12}>
          <HeaderIcon>
            <ChartBarIcon width={22} />
          </HeaderIcon>
          <div>
            <BoxTitle style={{ fontSize: "18px", fontWeight: 700 }}>Haftalık Devam Analizi</BoxTitle>
            <HaftaSatiri>
              <OkDugme
                type="button"
                aria-label="Önceki hafta"
                onClick={() => {
                  setOtomatikGeri(true); // elle gezildi; otomatik düşme bir daha denenmesin
                  setOtomatikGosterim(false);
                  setWeekOffset((o) => Math.max(-52, o - 1));
                }}
              >
                <ChevronLeftIcon width={14} />
              </OkDugme>
              <HaftaMetin>{haftaEtiketi}</HaftaMetin>
              <OkDugme
                type="button"
                aria-label="Sonraki hafta"
                disabled={weekOffset >= 0}
                onClick={() => {
                  setOtomatikGeri(true);
                  setOtomatikGosterim(false);
                  setWeekOffset((o) => Math.min(0, o + 1));
                }}
              >
                <ChevronRightIcon width={14} />
              </OkDugme>
              {weekOffset !== 0 && (
                <BuHaftayaDon
                  type="button"
                  onClick={() => {
                    setOtomatikGeri(true);
                    setOtomatikGosterim(false);
                    setWeekOffset(0);
                  }}
                >
                  Bu haftaya dön
                </BuHaftayaDon>
              )}
            </HaftaSatiri>
            {otomatikGosterim && (
              <OtomatikNot>Bu hafta henüz veri yok — geçen hafta gösteriliyor.</OtomatikNot>
            )}
          </div>
        </Flex>
        <Legend>
          {[
            { label: "Mevcut",      color: theme.colors.alert.green  },
            { label: "Erken Çıkış", color: theme.colors.alert.orange },
            { label: "Geç",         color: theme.colors.alert.red    },
          ].map((l) => (
            <LegendItem key={l.label}>
              <LegendDot $color={l.color} />
              <span>{l.label}</span>
            </LegendItem>
          ))}
        </Legend>
      </HeaderRow>

      <ChartWrapper>
        {loading ? (
          <Skeleton height="100%" width="100%" radius="xl" />
        ) : olculenGunYok ? (
          /*
            Hiçbir gün ölçülebilir değilse grafik ÇİZİLMİYOR. Yedi sıfır
            sütun "bu hafta kimse gelmedi" diye okunuyordu; oysa sebep
            genellikle haftaya hiç vardiya atanmamış olması.
          */
          <BosDurum>
            <BosBaslik>
              {weekOffset === 0 ? "Bu hafta" : "Seçili hafta"} için vardiya planı yok
            </BosBaslik>
            <BosMetin>
              Devam analizi, çalışanların atandığı vardiyalarla karşılaştırılarak
              hesaplanıyor. Takvimden vardiya atandığında bu grafik dolacak.
            </BosMetin>
          </BosDurum>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
              barGap={8}
            >
              <CartesianGrid vertical={false} stroke="#F1F5F9" strokeDasharray="3 3" />
              <XAxis
                axisLine={false}
                tickLine={false}
                dataKey="day"
                tick={<GunEtiketi veri={chartData} />}
                height={38}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: "#94a3b8", fontSize: 12, fontWeight: 500 }}
                tickFormatter={(v) => (v === 0 ? "" : v)}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: "#F8FAFC", radius: 10 }} />
              <Bar dataKey="present"   fill={theme.colors.alert.green}  radius={[6, 6, 0, 0]} barSize={16} />
              {/*
                ⚠ dataKey "earylExit" YAZILMIŞTI (harf devriği) — backend
                `earlyExit` gönderiyor, dolayısıyla turuncu çubuk HİÇ
                çizilmiyordu. Gösterge "Erken Çıkış" diyor, alttaki sayaç
                doğru sayıyı yazıyor, grafikte karşılığı yoktu. Veri sıfırken
                görünmeyen cinsten bir hata: yalnızca gerçek veriyle ortaya
                çıktı (plan 2.60).
              */}
              <Bar dataKey="earlyExit" fill={theme.colors.alert.orange} radius={[6, 6, 0, 0]} barSize={16} />
              <Bar dataKey="late"      fill={theme.colors.alert.red}    radius={[6, 6, 0, 0]} barSize={16} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartWrapper>

      <StatStrip>
        <StatCard $bg="#ECFDF5" $border="#A7F3D0">
          <StatIconCircle $color="#10B981" $bg="#10B98120">
            <ArrowTrendingUpIcon width={18} />
          </StatIconCircle>
          <div>
            <StatItemVal $color="#059669">%{stats?.averageAttendancePercent ?? 0}</StatItemVal>
            <StatItemLbl>Ort. Devam</StatItemLbl>
          </div>
        </StatCard>

        <StatCard $bg="#FFFBEB" $border="#FDE68A">
          <StatIconCircle $color="#D97706" $bg="#D9770620">
            <ClockIcon width={18} />
          </StatIconCircle>
          <div>
            <StatItemVal $color="#D97706">{stats?.totalEarlyExits ?? 0}</StatItemVal>
            <StatItemLbl>Erken Çıkış</StatItemLbl>
          </div>
        </StatCard>

        <StatCard $bg="#FEF2F2" $border="#FECACA">
          <StatIconCircle $color="#DC2626" $bg="#DC262620">
            <ArrowTrendingDownIcon width={18} />
          </StatIconCircle>
          <div>
            <StatItemVal $color="#DC2626">{stats?.totalLates ?? 0}</StatItemVal>
            <StatItemLbl>Geç Kalan</StatItemLbl>
          </div>
        </StatCard>
      </StatStrip>
    </Container>
  );
};

export default WeeklyAttendanceAnalysis;


const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 5px;

  ${media.mobile} {
    flex-wrap: wrap;
    gap: 12px;
  }
`;

const HeaderIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: ${theme.colors.brand.normal}12;
  color: ${theme.colors.brand.normal};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 1px solid ${theme.colors.brand.normal}25;
`;

const Legend = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  span {
    font-size: 13px;
    font-weight: 600;
    color: #475569;
  }
`;

const LegendDot = styled.div<{ $color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 4px;
  background: ${(p) => p.$color};
`;

const ChartWrapper = styled.div`
  flex: 1;
  min-height: 200px;
  width: 100%;
  margin: 14px 0 10px;
`;

const StatStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  width: 100%;

  /* Üç kart 390px'te 100px'e düşüyor; "Erken Çıkış" gibi etiketler sığmıyor. */
  ${media.mobile} {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div<{ $bg: string; $border: string }>`
  background: ${(p) => p.$bg};
  border: 1px solid ${(p) => p.$border};
  padding: 8px 14px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  gap: 12px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  animation: ${fadeInUp} 0.5s ease both;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.06);
  }
`;

const StatIconCircle = styled.div<{ $color: string; $bg: string }>`
  width: 36px;
  height: 36px;
  border-radius: 9px;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const StatItemVal = styled.div<{ $color: string }>`
  font-size: 20px;
  font-weight: 800;
  color: ${(p) => p.$color};
  line-height: 1.1;
`;

const StatItemLbl = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 1px;
`;

const TooltipBox = styled.div`
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(10px);
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 14px 18px;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TooltipLabel = styled.div`
  font-size: 14px;
  font-weight: 800;
  color: #1e293b;
  border-bottom: 1px solid #f1f5f9;
  padding-bottom: 8px;
  margin-bottom: 4px;
`;

const TooltipRow = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: #475569;
  font-weight: 500;
  strong { margin-left: auto; color: #0f172a; font-size: 14px; }
`;

const TooltipDot = styled.div<{ $color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 3px;
  background: ${(p) => p.$color};
`;